import React from 'react';
import {
  PaperClipOutlined, CloseCircleOutlined, CheckCircleOutlined, FileTextOutlined,
  CarOutlined, UserOutlined, HomeOutlined, InboxOutlined, WarningOutlined,
  MedicineBoxOutlined, ExperimentOutlined, SolutionOutlined, FileImageOutlined,
  InteractionOutlined, ToolOutlined, HeartOutlined, CheckOutlined,
  PauseCircleOutlined, SyncOutlined,
} from '@ant-design/icons';
import { HAB, HAB_PAR_PROFIL_LEGACY } from './habilitations';
import i18n from '../i18n';
import { avecLibelleTraduit, libellesTraduits } from '../i18n/libelles';
import { localeIntl } from '../i18n/format';

export const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8083/api';

export const getToken = () => sessionStorage.getItem('token') || null;

/**
 * Ecouteurs de presence du jeton.
 *
 * Un composant monte AU-DESSUS de la route /login - c'est le cas d'`AppelProvider`,
 * qui doit rester visible sur toutes les pages - voit `getToken()` a `null` a son
 * montage. Si son effet sort la-dessus et que ses dependances sont stables, il ne
 * se rejoue JAMAIS apres la connexion : l'abonnement STOMP n'est jamais pose et
 * l'utilisateur ne recoit plus une seule offre d'appel de toute sa session. Le
 * defaut est invisible apres un rechargement de page, ou le jeton est deja la -
 * d'ou un symptome qui semble aleatoire.
 *
 * D'ou ce signal : les composants concernes prennent le jeton en dependance.
 */
const ecouteursJeton = new Set();
const notifierJeton = () => ecouteursJeton.forEach((f) => {
  try { f(getToken()); } catch { /* ecouteur defaillant */ }
});

/** @returns fonction de desabonnement. */
export const surChangementJeton = (f) => {
  ecouteursJeton.add(f);
  return () => ecouteursJeton.delete(f);
};

export const setToken = (token) => {
  if (!token) return;
  sessionStorage.setItem('token', token);
  notifierJeton();
};

/**
 * Efface la session de l'onglet, SANS toucher au « Se souvenir de moi ».
 *
 * Un 401 fortuit - jeton d'accès expiré, serveur redémarré - ne prouve rien sur
 * la validité du jeton de rafraîchissement. Le détruire au passage supprimerait
 * la fonctionnalité au premier incident, alors que la session pourrait être
 * rouverte silencieusement au chargement suivant.
 */
export const clearSession = () => {
  sessionStorage.removeItem('token');
  sessionStorage.removeItem('user');
  // nettoyage ancien format n/t/k (rétrocompat)
  sessionStorage.removeItem('n');
  sessionStorage.removeItem('t');
  sessionStorage.removeItem('k');
  notifierJeton();
};

/**
 * Déconnexion complète : la session ET le « Se souvenir de moi ».
 * Réservé au départ volontaire de l'utilisateur, ou à un refus explicite du
 * serveur - jamais à un aléa de transport.
 */
export const clearToken = () => {
  clearSession();
  clearRefreshToken();
};

// ── « Se souvenir de moi » ──

/**
 * Jeton de rafraichissement, conserve UNIQUEMENT si l'utilisateur a coche
 * « Se souvenir de moi ».
 *
 * Le jeton d'acces reste en `sessionStorage` : il meurt avec l'onglet, comme
 * avant. Seul le jeton de rafraichissement survit, et il ne donne acces a rien
 * par lui-meme - `JwtAuthFilter` refuse un jeton de type `refresh` sur toute
 * ressource protegee. Il ne sert qu'a demander un nouvel acces sur
 * `/v1/auth/renouveler-token`, ou le serveur revalide la session, le compte et
 * l'appareil.
 *
 * `localStorage` et non un cookie `httpOnly` : c'est la ou vit deja le jeton
 * d'acces, envoye en en-tete `Bearer`. Passer au cookie demanderait de reprendre
 * l'emission, le CORS et la protection CSRF de toute la chaine - a faire, mais
 * pas en meme temps que de rendre la case fonctionnelle.
 *
 * Chaque acces est protege : un navigateur en navigation privee, ou regle pour
 * bloquer le stockage, fait lever ces API. Sans garde, la connexion echouerait
 * pour une simple preference de navigateur.
 */
const REFRESH_KEY = 'cb_refresh';
const REFRESH_USER_KEY = 'cb_refresh_user';

export const getRefreshToken = () => {
  try { return localStorage.getItem(REFRESH_KEY) || null; } catch { return null; }
};

/**
 * L'identite est conservee avec le jeton, et pour la meme raison.
 *
 * `/v1/auth/renouveler-token` ne rend que des jetons, et il n'existe pas
 * d'endpoint « mon profil » a rappeler. Or l'application a besoin de l'objet
 * utilisateur des son montage - l'intercepteur 401 renvoie vers /login des que
 * `getUser()` est nul. Sans ce miroir, le renouvellement rendrait un acces
 * valide a une application qui se croit deconnectee.
 *
 * Cette copie ne sert qu'a l'affichage : toutes les habilitations sont
 * revalidees cote serveur a chaque appel.
 */
export const setRefreshToken = (token, user) => {
  if (!token) return;
  try {
    localStorage.setItem(REFRESH_KEY, token);
    if (user) localStorage.setItem(REFRESH_USER_KEY, JSON.stringify(user));
  } catch { /* stockage indisponible */ }
};

export const getRefreshUser = () => {
  try {
    const raw = localStorage.getItem(REFRESH_USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
};

export const clearRefreshToken = () => {
  try {
    localStorage.removeItem(REFRESH_KEY);
    localStorage.removeItem(REFRESH_USER_KEY);
  } catch { /* stockage indisponible */ }
};

// ── OTP 2FA session helpers ──
const OTP_TRACKING_KEY = 'otpTrackingId';
const OTP_MASKED_EMAIL_KEY = 'otpMaskedEmail';
/* ÉCHÉANCES, PAS DURÉES. Ces clés portaient des durées en secondes, figées à
   l'écriture. Un rechargement de la page OTP relisait la durée d'origine et
   relançait le décompte depuis le début : le compte à rebours annonçait cinq
   minutes sur un code qui en avait déjà vécu quatre, et le renvoi se rebloquait
   pour soixante secondes de plus à chaque rechargement. Le temps passé hors de
   la page n'était jamais compté.

   On stocke donc l'INSTANT d'échéance, et le restant s'en déduit. Les noms
   changent avec le sens : une ancienne session, ouverte avant cette bascule,
   n'a pas ces clés et retombe sur les valeurs de repli plutôt que de se voir
   relire un nombre de secondes comme une date. */
const OTP_EXPIRE_KEY = 'otpExpireLe';
const OTP_RENVOI_KEY = 'otpRenvoiLe';
const OTP_DEBUT_KEY = 'otpDebutLe';
const OTP_VALIDATION_ADMIN_KEY = 'otpValidationAdminPossible';
const OTP_CONFIANCE_KEY = 'otpConfiancePossible';
const OTP_AUTHENTICATOR_KEY = 'otpAuthenticatorDisponible';
const OTP_AUTHENTICATOR_ASSOCIE_KEY = 'otpAuthenticatorAssocie';

/**
 * Les durées viennent du serveur et ne sont plus codées en dur dans l'écran :
 * une validité changée côté back (propriété `securite.appareils.otp`) doit se
 * voir dans le compte à rebours, sinon l'écran annonce un délai qui n'est plus
 * celui appliqué.
 */
export const setOtpSession = (trackingId, maskedEmail, options = {}) => {
  if (trackingId) {
    sessionStorage.setItem(OTP_TRACKING_KEY, trackingId);
    // Origine du temps passé sur l'écran, qui conditionne l'offre de recours.
    // Posée à la création seulement : un renvoi de code ne fait pas oublier
    // l'attente déjà subie - c'est précisément elle qui justifie le recours.
    if (!sessionStorage.getItem(OTP_DEBUT_KEY))
      sessionStorage.setItem(OTP_DEBUT_KEY, String(Date.now()));
  }
  if (maskedEmail) sessionStorage.setItem(OTP_MASKED_EMAIL_KEY, maskedEmail);
  if (options.expireDansSecondes != null)
    sessionStorage.setItem(OTP_EXPIRE_KEY, String(Date.now() + options.expireDansSecondes * 1000));
  if (options.renvoiPossibleDansSecondes != null)
    sessionStorage.setItem(OTP_RENVOI_KEY, String(Date.now() + options.renvoiPossibleDansSecondes * 1000));
  if (options.validationAdminPossible != null)
    sessionStorage.setItem(OTP_VALIDATION_ADMIN_KEY, options.validationAdminPossible ? '1' : '0');
  if (options.confiancePossible != null)
    sessionStorage.setItem(OTP_CONFIANCE_KEY, options.confiancePossible ? '1' : '0');
  // Voie de secours par application d'authentification. Simple indication
  // d'affichage : le serveur revérifie l'autorisation à chaque vérification,
  // forcer cette valeur ici n'ouvre aucune porte.
  if (options.authenticatorDisponible != null)
    sessionStorage.setItem(OTP_AUTHENTICATOR_KEY, options.authenticatorDisponible ? '1' : '0');
  // Deja associe ou non : l'ecran n'a pas la meme chose a proposer dans les deux cas.
  if (options.authenticatorAssocie != null)
    sessionStorage.setItem(OTP_AUTHENTICATOR_ASSOCIE_KEY, options.authenticatorAssocie ? '1' : '0');
};

export const getOtpTrackingId = () => sessionStorage.getItem(OTP_TRACKING_KEY) || null;
export const getOtpMaskedEmail = () => sessionStorage.getItem(OTP_MASKED_EMAIL_KEY) || null;
/* Secondes restantes avant une échéance stockée. `null` quand la clé est absente
   - jamais quand le reste vaut zéro : confondre les deux ferait afficher la durée
   de repli sur un code déjà périmé, soit exactement le mensonge qu'on corrige. */
const resteAvant = (cle) => {
  const brut = sessionStorage.getItem(cle);
  if (brut == null) return null;
  const echeance = Number(brut);
  if (!Number.isFinite(echeance)) return null;
  return Math.max(0, Math.ceil((echeance - Date.now()) / 1000));
};

export const getOtpExpireDansSecondes = () => resteAvant(OTP_EXPIRE_KEY);
export const getOtpRenvoiDansSecondes = () => resteAvant(OTP_RENVOI_KEY);

/** Secondes écoulées depuis l'ouverture de la session OTP, rechargements compris. */
export const getOtpSecondesEcoulees = () => {
  const debut = Number(sessionStorage.getItem(OTP_DEBUT_KEY));
  if (!Number.isFinite(debut) || debut <= 0) return 0;
  return Math.max(0, Math.floor((Date.now() - debut) / 1000));
};
// Par défaut à vrai : ne pas masquer le seul recours d'un agent bloqué parce que
// le serveur n'a rien précisé.
export const getOtpValidationAdminPossible = () =>
  sessionStorage.getItem(OTP_VALIDATION_ADMIN_KEY) !== '0';
// Par défaut à faux : ne jamais proposer une case qui ne ferait rien. Le serveur
// dit explicitement quand la confiance est possible.
export const getOtpConfiancePossible = () =>
  sessionStorage.getItem(OTP_CONFIANCE_KEY) === '1';
// Par défaut à faux : ne jamais proposer la case à qui n'a pas associé
// d'application - elle ne pourrait que produire un refus.
export const getOtpAuthenticatorDisponible = () =>
  sessionStorage.getItem(OTP_AUTHENTICATOR_KEY) === '1';

/** Le compte porte-t-il deja une application confirmee ? */
export const getOtpAuthenticatorAssocie = () =>
  sessionStorage.getItem(OTP_AUTHENTICATOR_ASSOCIE_KEY) === '1';

export const clearOtpSession = () => {
  sessionStorage.removeItem(OTP_TRACKING_KEY);
  sessionStorage.removeItem(OTP_MASKED_EMAIL_KEY);
  sessionStorage.removeItem(OTP_EXPIRE_KEY);
  sessionStorage.removeItem(OTP_RENVOI_KEY);
  sessionStorage.removeItem(OTP_DEBUT_KEY);
  sessionStorage.removeItem(OTP_VALIDATION_ADMIN_KEY);
  sessionStorage.removeItem(OTP_CONFIANCE_KEY);
  sessionStorage.removeItem(OTP_AUTHENTICATOR_KEY);
  sessionStorage.removeItem(OTP_AUTHENTICATOR_ASSOCIE_KEY);
};

// Délai avant déconnexion automatique du popup de re-auth (ms).
// Surchargeable via REACT_APP_REAUTH_TIMEOUT_MS au build.
export const REAUTH_TIMEOUT_MS =
  Number(process.env.REACT_APP_REAUTH_TIMEOUT_MS) || 5 * 60 * 1000;

// Temps sans geste au bout duquel le mot de passe est redemande (ms).
//
// Trente minutes : c'est la duree que l'ecran « Securite de l'organisme »
// annoncait, et celle que le manuel documente. Elle etait annoncee sans etre
// appliquee - la fenetre de re-saisie n'arrivait que sur un 401 du serveur, et
// « Se souvenir de moi » faisait reussir le renouvellement silencieux avant
// qu'elle ne paraisse.
//
// Surchargeable via REACT_APP_INACTIVITE_TIMEOUT_MS au build. Une valeur nulle
// ou negative DESARME le verrou plutot que de le refermer en boucle.
export const INACTIVITE_TIMEOUT_MS =
  Number(process.env.REACT_APP_INACTIVITE_TIMEOUT_MS) || 30 * 60 * 1000;

// Pas de la verification du verrou (ms). On lit l'horloge a chaque tour, donc
// ce pas ne borne que la LATENCE du verrouillage, jamais sa justesse.
export const INACTIVITE_PAS_VERIFICATION_MS = 15 * 1000;

export const getUser = () => {
  const raw = sessionStorage.getItem('user');
  if (!raw) return null;

  try {
    return JSON.parse(raw);
  } catch (e) {
    console.warn("User JSON invalide:", raw);
    return null;
  }
};

/**
 * Écrit l'utilisateur en session, SANS jamais perdre ses habilitations.
 *
 * POURQUOI CETTE PRÉCAUTION. Les habilitations n'accompagnent qu'une seule
 * réponse : celle de l'authentification (`UserInfoResponse`). `/v1/utilisateurs/me`
 * renvoie un `UtilisateurResponse`, qui décrit la même personne sans ce champ -
 * il sert à afficher une fiche, pas à décider d'un droit. Un écran qui ré-affichait
 * le profil écrasait donc la session avec une identité sans habilitations, et
 * `getHabilitations` retombait sur le catalogue de secours.
 *
 * Pour les profils qu'il connaît, ce repli masquait le problème. Pour les autres -
 * SUPERADMIN le premier, absent de ce catalogue puisqu'il est créé en base et non
 * codé en dur - il rendait une liste VIDE : l'administrateur le plus habilité de
 * l'application perdait tous ses droits d'écran en consultant sa propre fiche.
 *
 * On conserve donc la liste en place quand la nouvelle charge n'en apporte pas.
 * Une charge QUI en apporte une remplace bien l'ancienne : c'est le cas du login
 * et du renouvellement, les deux seules sources légitimes.
 */
export const setUser = (user) => {
  if (!user) return;
  const precedent = getUser();
  const aDesHabilitations = Array.isArray(user.habilitations);
  const fusionne = aDesHabilitations || !Array.isArray(precedent?.habilitations)
    ? user
    : { ...user, habilitations: precedent.habilitations };
  sessionStorage.setItem('user', JSON.stringify(fusionne));
};

/* ══════════════════════════════════════════════════════════════════════════
 * RBAC - on autorise sur les HABILITATIONS, jamais sur le nom du profil.
 *
 * CBConnect n'a pas de rôles figés : un administrateur CRÉE des profils
 * (table `profil`, nom libre) et leur attache des habilitations.
 * Autoriser sur `profilNom === 'SE'` casserait dès qu'un admin crée un profil :
 * celui-ci n'aurait accès à rien, quelles que soient ses habilitations.
 *
 * Le catalogue vit dans ./habilitations.js (généré depuis les @PreAuthorize).
 * ══════════════════════════════════════════════════════════════════════════ */
export { HAB } from './habilitations';

// Libellés d'affichage des profils connus. Purement cosmétique : un profil
// absent de cette table s'affiche tel quel, et ça ne change AUCUN droit.
export const PROFILS_LABELS = {
  SUPERADMIN: 'Super administrateur',
  ADMIN: 'Administrateur',
  SE: 'Secrétaire Exécutif',
  CSS: 'Chef de Service Sinistre',
  REDACTEUR: 'Rédacteur',
  GESTIONNAIRE: 'Gestionnaire',
  COMPTABLE: 'Comptable',
  SECRETAIRE: 'Secrétaire',
  COURSIER: 'Coursier',
};

// Le nom du profil n'a pas la même clé selon la source :
//   /v1/auth/login   → UserInfoResponse.profil
//   /v1/utilisateurs → UtilisateurResponse.profilNom
// Réservé à l'AFFICHAGE. Ne jamais s'en servir pour autoriser une action.
export const getProfil = (user) =>
  String(user?.profil ?? user?.profilNom ?? user?.role ?? '').toUpperCase();

// PROFILS_LABELS reste en français : son seul autre lecteur, BlocSignature, imprime
// la fonction du signataire sur des documents adressés à des tiers. L'écran passe
// par la traduction, avec le libellé français en repli.
export const libelleProfil = (user) => {
  const p = getProfil(user);
  const libelle = PROFILS_LABELS[p]
    ? i18n.t(`referentiels:profils.${p}`, { defaultValue: PROFILS_LABELS[p] })
    : null;
  return libelle || p || i18n.t('referentiels:profilParDefaut');
};

/** Habilitations de l'utilisateur connecté. Source de vérité = le backend. */
export const getHabilitations = (user) => {
  if (Array.isArray(user?.habilitations)) {
    return user.habilitations.map((h) =>
      String(typeof h === 'string' ? h : h?.codeHabilitation ?? '').toUpperCase());
  }
  // Session ouverte avant la bascule RBAC - voir habilitations.js.
  return HAB_PAR_PROFIL_LEGACY[getProfil(user)] ?? [];
};

/** Garde d'UI. `can(HAB.SINISTRE_ASSIGN)` - jamais `profil === 'CSS'`. */
export const can = (habilitation, user = getUser()) =>
  getHabilitations(user).includes(String(habilitation).toUpperCase());

export const canAny = (habilitations, user = getUser()) => {
  const mine = getHabilitations(user);
  return habilitations.some((h) => mine.includes(String(h).toUpperCase()));
};

export const canAll = (habilitations, user = getUser()) => {
  const mine = getHabilitations(user);
  return habilitations.every((h) => mine.includes(String(h).toUpperCase()));
};

// Télécharger le scan d'un PV depuis la GED. Sans cette habilitation,
// l'utilisateur garde l'accès en lecture (prévisualisation).
export const peutTelechargerPv = (user = getUser()) => can(HAB.GED_DOWNLOAD, user);

// Affecter / réaffecter un dossier sinistre à un utilisateur.
export const peutAffecterDossier = (user = getUser()) => can(HAB.SINISTRE_ASSIGN, user);

/* PROFILS_ADMIN et estProfilAdmin ont ete SUPPRIMES.
 *
 * Ils autorisaient par NOM de profil, et leur liste n'a jamais contenu SUPERADMIN :
 * l'ecran de securite est ainsi reste invisible au compte le plus habilite de
 * l'application. Les deux derniers ecrans qui s'en servaient sont passes a
 * can(HAB.X) ; les laisser exportes n'aurait servi qu'a voir le mecanisme
 * revenir ailleurs.
 *
 * Une garde d'UI s'ecrit can(HAB.X) - jamais profil === '...'. Les profils sont
 * crees en base avec un nom libre : tout code qui teste ce nom se trompe des
 * qu'un administrateur en cree un nouveau. */
export const roleUtilisateur = (user) =>
  String(user?.profilNom || user?.profil || '').trim().toUpperCase();

const ones = [
  "", "un", "deux", "trois", "quatre", "cinq", "six", "sept", "huit", "neuf",
  "dix", "onze", "douze", "treize", "quatorze", "quinze", "seize",
  "dix-sept", "dix-huit", "dix-neuf"
];

const tens = [
  "", "", "vingt", "trente", "quarante", "cinquante", "soixante"
];

// ── Statuts sinistre - machine d'état backend (StatutSinistre.java) ──
export const STATUTS_SINISTRE = avecLibelleTraduit([
  // SOUMISE precede NOUVEAU : c'est l'antichambre, ou une declaration attend d'etre
  // prise en charge. Les DEUX canaux y passent, et en sortent differemment : une
  // declaration du guichet entre dans le flux quand un redacteur se la voit affecter ;
  // une declaration mobile, quand un agent la valide dans l'ecran dedie. Le libelle ne
  // dit donc plus « mobile » : il dirait faux une fois sur deux.
  { value: 'SOUMISE', color: 'magenta' },
  { value: 'NOUVEAU', color: 'blue' },
  { value: 'VALIDE', color: 'cyan' },
  { value: 'GARANTIE_CONFIRMEE', color: 'geekblue' },
  // Le destinataire est dans le libellé : ET → bureau homologue, TE → compagnie
  // (voir services/partieGarantie).
  { value: 'GARANTIE_DEMANDEE', color: 'blue' },
  { value: 'GARANTIE_ATTENTE_CONFIRMATION', color: 'orange' },
  { value: 'GARANTIE_NON_ACQUISE', color: 'red' },
  { value: 'ATTENTE_PV', color: 'purple' },
  { value: 'ATTENTE_RC', color: 'orange' },
  { value: 'ATTENTE_PIECES_DE_RECLAMATION', color: 'gold' },
  { value: 'MUR', color: 'volcano' },
  { value: 'OFFRE_EFFECTUE', color: 'lime' },
  { value: 'ATTENTE_OFFRE', color: 'yellow' },
  { value: 'OFFRE_RECUE', color: 'green' },
  { value: 'ACCORD', color: 'success' },
  { value: 'REJET', color: 'red' },
  { value: 'BAP', color: 'lime' },
  { value: 'ATTENTE_CHEQUE', color: 'gold' },
  { value: 'CHEQUE_RECU', color: 'cyan' },
  { value: 'PARTIELLEMENT_PAYE', color: 'orange' },
  { value: 'SOLDE', color: 'success' },
  { value: 'CLOTURE', color: 'default' },
  { value: 'REOUVERT', color: 'blue' },
  { value: 'CONTENTIEUX', color: 'red' },
  { value: 'ARBITRAGE', color: 'purple' },
  { value: 'GARANTIE_REJETEE', color: 'red' },
  { value: 'SANS_SUITE', color: 'default' },
], 'referentiels:statutsSinistre');

/**
 * Regroupement des 27 statuts en phases du cycle de vie, pour les indicateurs cles
 * de la liste des sinistres. La partition doit rester EXHAUSTIVE et DISJOINTE :
 * un statut oublie disparaitrait des agregats tout en restant compte dans le total,
 * et les cartes cesseraient de s'additionner sans que rien ne le signale.
 * Verrouille par src/variables/__tests__/phasesSinistre.test.js.
 *
 * CONTENTIEUX et ARBITRAGE sont rattaches a l'instruction : un dossier en litige
 * reste un dossier a traiter.
 *
 * Les couleurs viennent de la palette ANTD_COLOR_HEX de ListSinistres.js (magenta,
 * blue, gold, default) ; le gris signale l'etat terminal sans attirer l'oeil.
 */
export const PHASES_SINISTRE = avecLibelleTraduit([
  {
    // Phase a part, et non fondue dans INSTRUCTION : une declaration SOUMISE n'est
    // pas encore un dossier a instruire, elle attend d'etre prise en charge. La noyer
    // dans « En instruction » gonflerait l'indicateur de charge des agents avec des
    // declarations que personne ne traite -- et rendrait la corbeille invisible.
    key: 'DECLARATION',
    color: '#db2777',
    statuts: ['SOUMISE'],
  },
  {
    key: 'INSTRUCTION',
    color: '#2563eb',
    statuts: [
      'NOUVEAU', 'VALIDE', 'GARANTIE_CONFIRMEE', 'GARANTIE_DEMANDEE', 'GARANTIE_ATTENTE_CONFIRMATION',
      'ATTENTE_PV', 'ATTENTE_RC', 'ATTENTE_PIECES_DE_RECLAMATION', 'MUR', 'REOUVERT', 'CONTENTIEUX', 'ARBITRAGE',
    ],
  },
  {
    key: 'REGLEMENT',
    color: '#ca8a04',
    statuts: [
      'OFFRE_EFFECTUE', 'ATTENTE_OFFRE', 'OFFRE_RECUE', 'ACCORD', 'BAP',
      'ATTENTE_CHEQUE', 'CHEQUE_RECU', 'PARTIELLEMENT_PAYE',
    ],
  },
  {
    key: 'CLOTURE',
    color: '#6b7280',
    statuts: ['GARANTIE_NON_ACQUISE', 'REJET', 'SOLDE', 'CLOTURE', 'GARANTIE_REJETEE', 'SANS_SUITE'],
  },
], 'referentiels:phasesSinistre', { cle: 'key' });

/** Somme des compteurs des statuts d'une phase. `counts` : objet { [statut]: nombre }. */
export const compterPhase = (counts, phase) =>
  phase.statuts.reduce((n, s) => n + (counts?.[s] ?? 0), 0);

/**
 * Somme de TOUS les statuts connus. C'est le total a afficher : contrairement au
 * `total` de la pagination, il ignore les filtres du tableau - exactement comme
 * `kpiCounts`, dont il derive. Melanger les deux ferait que le total puisse
 * devenir inferieur a l'une de ses propres phases.
 */
export const compterTousStatuts = (counts) =>
  STATUTS_SINISTRE.reduce((n, s) => n + (counts?.[s.value] ?? 0), 0);

// Le libellé du statut d'attente de garantie dépend du type de sinistre.
// SURVENU_TOGO (ET) : demandée au bureau homologue ; SURVENU_ETRANGER (TE) : demandée à la compagnie.
export const libelleStatutGarantie = (statut, typeSinistre) => {
  if (statut === 'GARANTIE_DEMANDEE' || statut === 'GARANTIE_ATTENTE_CONFIRMATION') {
    return typeSinistre === 'SURVENU_ETRANGER'
      ? i18n.t('referentiels:statutGarantie.compagnie')
      : i18n.t('referentiels:statutGarantie.bureauHomologue');
  }
  const found = STATUTS_SINISTRE.find((s) => s.value === statut);
  return found ? found.label : statut;
};

export const frenchDate = (date) => {
  if (date) {
    const [datePart, timePart] = date?.split("T");
    const [year, month, day] = datePart?.split("-");
    return `${day}/${month}/${year} - ${timePart?.split(".")[0] || ""}`;
  } else {
    return "";
  }

};

export const frenchDateOnly = (date) => {
  if (date) {
    const [year, month, day] = date?.split("T")[0]?.split("-");
    return `${day}/${month}/${year}`;
  } else {
    return "";
  }

};

export const frenchDateSimple = (date) => {
  const [datePart, timePart] = date.split("T");
  const [year, month, day] = datePart.split("-");
  return `${day}/${month}/${year} - ${timePart || ""}`;
};

// Reste en 'fr-FR' : ne sert qu'aux documents imprimés (ordre de dépense, quittance),
// dont la langue est celle du destinataire, pas celle de l'agent.
export const displayNumbers = (value) => {
  return value && new Intl.NumberFormat('fr-FR').format(Math.floor(value))
};

export const STATUT_MAP = STATUTS_SINISTRE.reduce((acc, s) => {
  acc[s.value] = s; return acc;
}, {});

// Statuts à partir desquels le dossier est considéré « mûr ou plus » :
// les actions financières (encaissement, paiement, préfinancement) sont autorisées.
// Doit rester aligné avec MaturiteGuardServiceImpl côté backend.
export const STATUTS_DOSSIER_MUR_OU_PLUS = [
  'MUR', 'OFFRE_EFFECTUE', 'ATTENTE_OFFRE', 'OFFRE_RECUE', 'ACCORD', 'REJET',
  'BAP', 'ATTENTE_CHEQUE', 'CHEQUE_RECU', 'PARTIELLEMENT_PAYE', 'SOLDE',
  'CLOTURE', 'REOUVERT', 'CONTENTIEUX', 'ARBITRAGE',
];

export const isDossierMurOuPlus = (statut) =>
  !!statut && STATUTS_DOSSIER_MUR_OU_PLUS.includes(statut);

// Éditabilité de la déclaration : possible tant que le dossier n'est pas clôturé
// (aligné avec StatutSinistre.isEditableDeclaration() côté backend). À distinguer de
// isDossierMurOuPlus, qui borne les actions financières (préfinancement, paiement).
export const isDossierCloture = (statut) => statut === 'CLOTURE';

export const TYPES_SINISTRE = avecLibelleTraduit([
  { value: 'SURVENU_TOGO' },
  { value: 'SURVENU_ETRANGER' },
], 'referentiels:typesSinistre');

// ── Canal de saisie du dossier (CBConnect Mobile) ──
// Le backend applique WEB par defaut aux dossiers existants : un `canal` absent de
// la reponse se lit donc comme WEB, jamais comme « inconnu ».
export const CANAUX_SINISTRE = avecLibelleTraduit([
  { value: 'WEB', color: 'default' },
  { value: 'MOBILE', color: 'magenta' },
], 'referentiels:canauxSinistre');

export const CANAL_MAP = CANAUX_SINISTRE.reduce((acc, c) => {
  acc[c.value] = c; return acc;
}, {});

export const estCanalMobile = (canal) => canal === 'MOBILE';

// Type d'expertise - aligné avec TypeExpertise.java. INITIALE s'affiche « Expertise »
// (demande du 2026-09-15) : la valeur enregistrée ne change pas.
export const TYPES_EXPERTISE = avecLibelleTraduit([
  { value: 'INITIALE' },
  { value: 'CONTRE_EXPERTISE' },
  { value: 'AMIABLE' },
  { value: 'TIERCE_EXPERTISE' },
], 'referentiels:typesExpertise');
export const libelleTypeExpertise = (v) => TYPES_EXPERTISE.find((t) => t.value === v)?.label || v;

export const TYPES_DOMMAGE = avecLibelleTraduit([
  { value: 'CORPOREL' },
  { value: 'MATERIEL' },
  { value: 'MIXTE' },
], 'referentiels:typesDommage');

export const TYPES_VICTIME = avecLibelleTraduit([
  { value: 'BLESSE', color: '#b45309', bg: '#fef3c7' },
  { value: 'DECEDE', color: '#b42318', bg: '#fef3f2' },
  { value: 'NEUTRE', color: '#475569', bg: '#f1f5f9' },
], 'referentiels:typesVictime');

/**
 * Libellé d'une victime dans une liste déroulante : son nom, puis son type.
 *
 * POURQUOI LE TYPE Y FIGURE. Un dossier porte souvent plusieurs victimes, et les
 * homonymes sont fréquents dans un même foyer. « KODJO Ama » seul n'apprend rien :
 * l'agent ne sait pas s'il désigne la blessée ou la personne décédée, et le choix
 * n'a pas les mêmes suites - ni la même offre, ni les mêmes pièces.
 *
 * DEUX TYPES, PAS UN. `typeVictime` dit ce qu'elle a subi (blessée, décédée) ;
 * `typeDommage` dit sur quoi porte l'expertise (corporel, matériel, mixte). Les
 * deux sont affichés quand ils existent : lequel compte dépend de l'écran, et
 * n'en montrer qu'un obligerait chaque appelant à choisir - mal.
 *
 * Une partie adverse blessée est aussi une victime du dossier : elle est signalée
 * comme telle, faute de quoi on la confondrait avec l'assuré.
 */
export const libelleVictime = (v) => {
  if (!v) return '';
  const nom = [v.prenoms, v.nom].filter(Boolean).join(' ').trim()
    || v.nomComplet
    || v.victimeNomPrenom
    || v.victimeTrackingId
    || i18n.t('referentiels:victime.parDefaut');

  const types = [];
  const typeV = TYPES_VICTIME.find((t) => t.value === v.typeVictime);
  if (typeV) types.push(typeV.label);
  const typeD = TYPES_DOMMAGE.find((t) => t.value === v.typeDommage);
  if (typeD) types.push(i18n.t('referentiels:victime.dommage', { type: typeD.label.toLowerCase() }));
  if (v.estAdversaire) types.push(i18n.t('referentiels:victime.partieAdverse'));

  return types.length ? `${nom} - ${types.join(', ')}` : nom;
};

// Nature de l'adversaire - doit rester aligné avec TypeAdversaire.java côté backend.
// Seul ENGINS porte des champs véhicule (immatriculation, permis, remorque…).
export const TYPES_ADVERSAIRE = avecLibelleTraduit([
  { value: 'ENGINS' },
  { value: 'PIETON' },
  { value: 'BATIMENT' },
  { value: 'MEUBLE' },
  { value: 'AUTRE' },
], 'referentiels:typesAdversaire');

export const estAdversaireEngin = (type) => (type || 'ENGINS') === 'ENGINS';

/**
 * Partie adverse blessée ou décédée : elle est AUSSI une victime du dossier.
 * Exemple métier : l'assuré percute X dans sa voiture ; X subit un dommage matériel
 * et corporel - il reste une partie adverse (sa voiture, son assureur, la RC) et il
 * devient une victime à indemniser (offre, pièces, maturité).
 */
export const estAdversaireBlesse = (v) => !!v?.estAdversaire
  && (v.typeVictime === 'BLESSE' || v.typeVictime === 'DECEDE');

/**
 * Personne à indemniser : toute fiche victime, et toute partie adverse blessée ou
 * décédée. À utiliser partout où l'on écrivait `!v.estAdversaire` pour dire
 * « les victimes » - sinon une partie adverse blessée n'aurait ni offre ni pièces.
 */
export const estVictimeIndemnisable = (v) => !!v && (!v.estAdversaire || estAdversaireBlesse(v));

/* ─────────────────────────────────────────────────────────────────────────
   Natures de partie adverse - ce que le rédacteur choisit en premier écran.
   C'est une vue « métier » plus fine que l'enum backend TypeAdversaire :
   Moto et Tricycle sont des ENGINS dont le genre est déjà connu, on évite
   donc de le redemander à l'étape Véhicule.
   ───────────────────────────────────────────────────────────────────────── */
/* Le champ s'appelle `icone` et non plus `emoji` : il porte desormais un element
   React, et un nom qui annonce un caractere ferait ecrire `${nature.emoji}` dans un
   gabarit de chaine - ce qui donne « [object Object] » a l'ecran. Renommer force
   chaque appelant a etre relu. */
export const NATURES_PARTIE_ADVERSE = avecLibelleTraduit(avecLibelleTraduit([
  {
    key: 'VEHICULE', icone: <CarOutlined />,
    typeAdversaire: 'ENGINS',
  },
  {
    key: 'PIETON', icone: <UserOutlined />,
    typeAdversaire: 'PIETON',
  },
  {
    key: 'BATIMENT', icone: <HomeOutlined />,
    typeAdversaire: 'BATIMENT',
  },
  {
    key: 'MEUBLE', icone: <InboxOutlined />,
    typeAdversaire: 'MEUBLE',
  },
  {
    key: 'AUTRE', icone: <WarningOutlined />,
    typeAdversaire: 'AUTRE',
  },
], 'referentiels:naturesPartieAdverse.libelles', { cle: 'key' }),
'referentiels:naturesPartieAdverse.descriptions', { cle: 'key', champ: 'desc' });

export const NATURE_PARTIE_ADVERSE_MAP = NATURES_PARTIE_ADVERSE
  .reduce((acc, n) => { acc[n.key] = n; return acc; }, {});

/* Retrouve la nature à afficher pour un adversaire déjà enregistré.
   Le genre précis de l'engin (moto, tricycle, camion…) se choisit à l'étape
   Véhicule : il ne dédouble pas la liste des natures. */
export const natureDepuisAdversaire = (a) => {
  const type = a?.typeAdversaire || 'ENGINS';
  return NATURES_PARTIE_ADVERSE.find(n => n.typeAdversaire === type)
    || NATURE_PARTIE_ADVERSE_MAP.VEHICULE;
};

// L'API est asymétrique sur les catégories de permis : VictimeResponse renvoie une
// CHAÎNE ("A1,B") mais VictimeRequest attend une LISTE. On normalise toujours en tableau
// avant de renvoyer au backend (et pour alimenter les multi-select).
export const splitCategoriesPermis = (val) =>
  Array.isArray(val)
    ? val
    : (typeof val === 'string' && val ? val.split(',').map((c) => c.trim()).filter(Boolean) : []);

// Statut métier d'une victime (sous-dossier du sinistre).
// Chaque victime avance à son propre rythme dans le workflow.
export const STATUTS_VICTIME = avecLibelleTraduit([
  { value: 'NEUTRE', color: 'default' },
  { value: 'ATTENTE_PIECES', color: 'gold', icon: <PaperClipOutlined /> },
  { value: 'OFFRE_EFFECTUE', color: 'lime' },
  { value: 'ACCORD', color: 'success' },
  { value: 'REJET', color: 'red' },
  { value: 'BAP', color: 'lime' },
  { value: 'SOLDE', color: 'success' },
], 'referentiels:statutsVictime');
export const STATUT_VICTIME_MAP = STATUTS_VICTIME.reduce((a, s) => (a[s.value] = s, a), {});

// Niveaux de juridiction utilisés pour un dossier CONTENTIEUX.
export const NIVEAUX_JURIDICTION = avecLibelleTraduit([
  { value: 'TRIBUNAL_1ERE_INSTANCE' },
  { value: 'COUR_APPEL' },
  { value: 'COUR_CASSATION' },
  { value: 'TRIBUNAL_COMMERCE' },
  { value: 'AUTRE' },
], 'referentiels:niveauxJuridiction');

// ── Négociation RC par adversaire (V27) ──
export const POSITIONS_RC = avecLibelleTraduit([
  { value: 'EN_ATTENTE', color: '#475569', bg: '#f1f5f9', icon: <PauseCircleOutlined /> },
  { value: 'EN_NEGOCIATION', color: '#b45309', bg: '#fff7ed', icon: <SyncOutlined /> },
  { value: 'REJETEE', color: '#b42318', bg: '#fef2f2', icon: <CloseCircleOutlined /> },
  { value: 'TRANCHEE', color: '#15803d', bg: '#ecfdf5', icon: <CheckCircleOutlined /> },
], 'referentiels:positionsRc');
export const POSITION_RC_MAP = POSITIONS_RC.reduce((a, p) => (a[p.value] = p, a), {});

export const ACTIONS_RC = {
  PROPOSER: 'PROPOSER',
  REJETER: 'REJETER',
  ACCEPTER: 'ACCEPTER',
  TRANCHER: 'TRANCHER',
};

export const STATUTS_ACTIVITE = avecLibelleTraduit([
  { value: 'ACTIF' },
  { value: 'SANS_EMPLOI' },
  { value: 'ELEVE_ETUDIANT' },
  { value: 'RETRAITE' },
], 'referentiels:statutsActivite');

// ── Permis de conduire (série standard utilisée au Togo / CEDEAO) ──
export const CATEGORIES_PERMIS = avecLibelleTraduit([
  { value: 'A' },
  { value: 'A1' },
  { value: 'B' },
  { value: 'C' },
  { value: 'C1' },
  { value: 'D' },
  { value: 'D1' },
  { value: 'EB' },
  { value: 'EC' },
  { value: 'ED' },
], 'referentiels:categoriesPermis');

// ── Qualité du déclarant (lien avec l'assuré) ──
export const QUALITES_DECLARANT = avecLibelleTraduit([
  { value: 'ASSURE' },
  { value: 'CONDUCTEUR' },
  { value: 'PROCHE' },
  { value: 'TEMOIN' },
  { value: 'ENVOYE' },
  // Déclaration transmise directement par le bureau homologue.
  { value: 'BUREAU_HOMOLOGUE' },
  { value: 'AUTRE' },
], 'referentiels:qualitesDeclarant');

export const TYPES_EXPERT = avecLibelleTraduit([
  { value: 'MEDICAL' },
  { value: 'AUTOMOBILE' },
], 'referentiels:typesExpert');

export const STATUTS_CHEQUE = avecLibelleTraduit([
  { value: 'RECU', color: 'blue' },
  { value: 'ENCAISSE', color: 'green' },
  { value: 'ANNULE', color: 'red' },
], 'referentiels:statutsCheque');

export const STATUTS_PAIEMENT = avecLibelleTraduit([
  { value: 'EMIS', color: 'blue' },
  { value: 'PAYE', color: 'green' },
  { value: 'ANNULE', color: 'red' },
], 'referentiels:statutsPaiement');

// ── PV de sinistre ──
export const PV_ELEMENT_STATUTS = avecLibelleTraduit([
  { value: 'OK', color: 'green', icon: <CheckOutlined /> },
  { value: 'NEANT', color: 'default', icon: '-' },
], 'referentiels:pvElementStatuts');

export const PV_PROVENANCES = avecLibelleTraduit([
  { value: 'BUREAU_HOMOLOGUE' },
  { value: 'SCP' },
  { value: 'COMPAGNIE' },
  { value: 'PARTICULIER' },
  { value: 'AUTORITE' },
  { value: 'AUTRE' },
], 'referentiels:pvProvenances');

export const PV_SENS_CIRCULATION = avecLibelleTraduit([
  { value: 'ET' },
  { value: 'TE' },
], 'referentiels:pvSensCirculation');

// ── Réclamations ──
export const TYPES_DEPENSE = avecLibelleTraduit([
  { value: 'FRAIS_HOSPITALISATION', icon: <MedicineBoxOutlined /> },
  { value: 'PHARMACIE', icon: <ExperimentOutlined /> },
  { value: 'CONSULTATION', icon: <SolutionOutlined /> },
  { value: 'IMAGERIE', icon: <FileImageOutlined /> },
  { value: 'KINESITHERAPIE', icon: <InteractionOutlined /> },
  { value: 'PROTHESE', icon: <ToolOutlined /> },
  { value: 'TRANSPORT_MEDICAL', icon: <CarOutlined /> },
  { value: 'FRAIS_FUNERAIRES', icon: <HeartOutlined /> },
  { value: 'AUTRE', icon: <FileTextOutlined /> },
], 'referentiels:typesDepense');

export const STATUTS_FACTURE_RECLAMATION = avecLibelleTraduit([
  { value: 'EN_ATTENTE', color: 'gold' },
  { value: 'VALIDE', color: 'green' },
  { value: 'VALIDE_PARTIELLEMENT', color: 'orange' },
  { value: 'REJETE', color: 'red' },
], 'referentiels:statutsFactureReclamation');

export const STATUT_FACTURE_MAP = STATUTS_FACTURE_RECLAMATION.reduce((acc, s) => {
  acc[s.value] = s; return acc;
}, {});

export const STATUTS_DOSSIER_RECLAMATION = avecLibelleTraduit([
  { value: 'OUVERT', color: 'blue' },
  { value: 'EN_TRAITEMENT', color: 'gold' },
  { value: 'COMPLETE', color: 'green' },
  { value: 'CLOTURE', color: 'success' },
  { value: 'REJETE', color: 'red' },
], 'referentiels:statutsDossierReclamation');

export const STATUT_DOSSIER_MAP = STATUTS_DOSSIER_RECLAMATION.reduce((acc, s) => {
  acc[s.value] = s; return acc;
}, {});

// ── Types de documents GED (TypeDocumentOssanGed.java) avec libellés français ──
export const TYPES_DOC_GED = avecLibelleTraduit([
  { value: 'EXPERTISE_MED' },
  { value: 'EXPERTISE_AUTO' },
  { value: 'CMI' },
  { value: 'CMC' },
  { value: 'PV' },
  { value: 'FACTURE_MED' },
  { value: 'FACTURE_RECLAMATION' },
  { value: 'ORDONNANCE' },
  { value: 'FRAIS_FUNERAIRES' },
  { value: 'ACTE_DECES' },
  { value: 'ACTE_MARIAGE' },
  { value: 'ACTE_NAISSANCE' },
  { value: 'CNI' },
  { value: 'CERTIFICAT_VIE' },
  { value: 'SCOLARITE' },
  { value: 'COURRIER' },
  { value: 'OFFRE' },
  { value: 'ACCORD' },
  { value: 'FACTURE_ATTESTATION' },
  { value: 'RAPPORT_MEDICAL' },
  { value: 'FICHE_DECLARATION_HOMOLOGUE' },
  { value: 'ATTESTATION_DECLARATION' },
  { value: 'AUTRES' },
], 'referentiels:typesDocGed');

// Table code → libellé, lue dans la langue courante (une copie faite au chargement
// garderait celle du démarrage).
export const TYPES_DOC_GED_MAP = libellesTraduits('referentiels:typesDocGed');

// NOTE : les types de pièces ne sont plus en dur - ils sont paramétrables côté
// backend via /v1/parametrage/pieces (cf. page Paramétrage). La checklist
// par dossier est servie par /v1/dossiers/{id}/pieces.

export const NIVEAUX_ALERTE = avecLibelleTraduit([
  { value: 'NORMAL', color: '#1a6b3c' },
  { value: 'ATTENTION', color: '#d5c93b' },
  { value: 'URGENT', color: '#f97316' },
  { value: 'CRITIQUE', color: '#ef4444' },
], 'referentiels:niveauxAlerte');

// ── Formatage ──
// Espace insécable forcé comme séparateur de milliers (zone XOF / FCFA).
// Indépendant de la locale ICU du navigateur (qui peut produire `,` selon l'env).
export const formatMontant = (v) => {
  if (v == null) return '-';
  const n = Number(v);
  if (!Number.isFinite(n)) return '-';
  const sign = n < 0 ? '-' : '';
  const abs = Math.abs(n);
  const entier = Math.trunc(abs);
  const entierFormate = String(entier).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  const decimales = abs - entier;
  if (decimales > 0) {
    const decStr = decimales.toFixed(2).slice(2);
    return `${sign}${entierFormate},${decStr} FCFA`;
  }
  return `${sign}${entierFormate} FCFA`;
};

// Formatte un nombre avec espaces comme séparateurs de milliers (sans unité)
export const formatNumber = (v) => {
  if (v == null || v === '') return '-';
  return new Intl.NumberFormat(localeIntl()).format(v);
};

// Formatter / parser à utiliser sur les <InputNumber> AntD pour afficher
// les séparateurs de milliers pendant la saisie (ex: 1 000 000).
// Préserve les décimales (ex: 1 234.56).
export const numberInputFormatter = (v) => {
  if (v === null || v === undefined || v === '') return '';
  const [intPart, decPart] = String(v).split('.');
  const intFormatted = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return decPart !== undefined ? `${intFormatted}.${decPart}` : intFormatted;
};
export const numberInputParser = (v) => (v == null ? '' : String(v).replace(/\s/g, ''));

export const numberToWordsFrench = (num) => {
  if (num === 0) return "ZÉRO";

  if (num < 0) return "MOINS " + numberToWordsFrench(-num);

  const parts = [];

  const milliards = Math.floor(num / 1_000_000_000);
  if (milliards) {
    parts.push(`${numberToWordsFrench(milliards)} milliard${milliards > 1 ? "s" : ""}`);
    num %= 1_000_000_000;
  }

  const millions = Math.floor(num / 1_000_000);
  if (millions) {
    parts.push(`${numberToWordsFrench(millions)} million${millions > 1 ? "s" : ""}`);
    num %= 1_000_000;
  }

  const milliers = Math.floor(num / 1000);
  if (milliers) {
    if (milliers === 1) {
      parts.push("mille");
    } else {
      parts.push(`${numberToWordsFrench(milliers)} mille`);
    }
    num %= 1000;
  }

  if (num > 0) {
    parts.push(getBelowThousand(num));
  }

  return parts.join(" ").trim().toUpperCase();
};


function getBelowThousand(n) {
  if (n < 100) return getBelowHundred(n);

  const hundred = Math.floor(n / 100);
  const rest = n % 100;

  let prefix = hundred === 1 ? "cent" : ones[hundred] + " cent";

  // "cents" takes 's' only if no remainder
  if (rest === 0 && hundred > 1) {
    prefix += "s";
  }

  return rest ? `${prefix} ${getBelowHundred(rest)}` : prefix;
}

function getBelowHundred(n) {
  if (n < 20) return ones[n];

  if (n < 70) {
    const ten = Math.floor(n / 10);
    const unit = n % 10;
    if (unit === 1 && (ten === 2 || ten === 3 || ten === 4 || ten === 5 || ten === 6)) {
      return `${tens[ten]} et un`;
    } else {
      return `${tens[ten]}${unit ? "-" + ones[unit] : ""}`;
    }
  }

  if (n < 80) {
    const base = "soixante";
    return `${base}-${getBelowHundred(n - 60)}`;
  }

  if (n < 100) {
    const base = "quatre-vingt";
    if (n === 80) return base + "s";
    return `${base}-${getBelowHundred(n - 80)}`;
  }

  return "";
}

export const formatDate = (d) => {
  if (!d) return '-';
  // Si la valeur est une date pure (LocalDate "YYYY-MM-DD" sans heure),
  // on n'affiche PAS "à 00:00:00" qui est trompeur - on affiche juste la date.
  const isDateOnly = typeof d === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(d);
  const dt = new Date(d);
  if (isDateOnly) {
    return i18n.t('referentiels:dates.le', { date: dt.toLocaleDateString(localeIntl()) });
  }
  // Sinon (LocalDateTime/Instant), on affiche date + heure réelles (HH:MM, sans secondes).
  return i18n.t('referentiels:dates.leA', {
    date: dt.toLocaleDateString(localeIntl()),
    heure: dt.toLocaleTimeString(localeIntl(), { hour: '2-digit', minute: '2-digit' }),
  });
};

export const formatDateHeure = (d) => {
  if (!d) return '-';
  return new Date(d).toLocaleString(localeIntl(), {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

export const AVATAR_COLORS = [
  '#1a6b3c', '#2e7d32', '#1565c0', '#6a1b9a',
  '#e65100', '#4e342e', '#00695c', '#283593',
];

export const APP_COLORS = {
  SUCCES: '#1a6b3c',
  ERROR: '#e65100',
};

// `code` : clé du libellé dans la traduction (referentiels:forceMotDePasse).
export const PASSWORD_STRENGTH_COLOR = avecLibelleTraduit([
  { code: 'TRES_FAIBLE', color: '#ef4444' },
  { code: 'FAIBLE', color: '#f97316' },
  { code: 'MOYEN', color: '#eab308' },
  { code: 'BON', color: '#84cc16' },
  { code: 'FORT', color: '#22c55e' },
  { code: 'EXCELLENT', color: '#16a34a' },
], 'referentiels:forceMotDePasse', { cle: 'code' });


// ── Charte
export const CHARTE_COLORS = {
  green: '#155731',
  greenMid: '#1a6b3c',
  greenLight: '#e8f5ed',
  greenRing: 'rgba(21,87,49,0.12)',
  greenPale: '#e8f5ed',
  border: '#e5e7eb',
  bg: '#f9fafb',
  text: '#111827',
  muted: '#6b7280',
  faint: '#9ca3af',
  amber: '#b45309',
  amberBg: '#fef3c7',
  red: '#dc2626',
  redBg: '#fee2e2',
  blue: '#1d4ed8',
  blueBg: '#dbeafe',
};